import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getApiPartnersByIdOptions,
  getApiPartnersOptions,
  getApiProductsGroupsOptions,
  getApiSampleTestsOptions,
  type GetApiPartnersByIdError,
  type GetApiPartnersError,
  type GetApiProductsGroupsError,
  type GetApiSampleTestsError,
  type PageOfPartnerDto,
  type PageOfSampleTestDto,
  type PartnerDto,
} from '@/api';

import {
  toSampleTestsQuery,
  type ResolvedSampleTestsSearch,
} from '../lib/searchParams';

/*
 * Чтение плана тестирования образцов и справочников его фильтров. Типы возврата
 * заданы явно (TS2742); ключи - кодогенератора: запись теста сбрасывает
 * [{ _id: 'getApiSampleTests' }], а с ним и блок «Моих задач».
 */

/** Сколько клиентов показывает поле фильтра: дальше пользователь уточняет поиск. */
const PARTNER_OPTIONS_LIMIT = 20;

/** Тесты образцов, страница по фильтрам из адреса. */
export const useSampleTestsList = (
  resolved: ResolvedSampleTestsSearch,
): UseQueryResult<PageOfSampleTestDto, GetApiSampleTestsError> =>
  useQuery({
    ...getApiSampleTestsOptions({ query: toSampleTestsQuery(resolved) }),
    placeholderData: (previous) => previous,
  });

/** Клиенты для поля фильтра - по подстроке кода или наименования. */
export const usePartnerOptions = (
  search: string,
): UseQueryResult<PageOfPartnerDto, GetApiPartnersError> =>
  useQuery({
    ...getApiPartnersOptions({
      query: {
        Search: search === '' ? undefined : search,
        PageSize: PARTNER_OPTIONS_LIMIT,
      },
    }),
    placeholderData: (previous) => previous,
  });

/** Клиент из адреса - подпись поля фильтра после перезагрузки страницы. */
export const usePartner = (
  partnerId: string | undefined,
): UseQueryResult<PartnerDto, GetApiPartnersByIdError> =>
  useQuery({
    ...getApiPartnersByIdOptions({ path: { id: partnerId ?? '' } }),
    enabled: partnerId !== undefined,
  });

/**
 * Группы номенклатуры для фильтра - у всех позиций справочника, включая снятые:
 * образец мог уйти и снятым товаром. Групп тестов отдельно API не отдаёт.
 */
export const useProductGroups = (): UseQueryResult<
  Array<string>,
  GetApiProductsGroupsError
> => useQuery(getApiProductsGroupsOptions({ query: { Activity: 'All' } }));
